import { useAppState } from '../context/State';
import { GetCopy } from '../util/GetCopy';
import Device from './Device';
import { ReactComponent as ArrowRight } from '../../src/img/arrowUpRight.svg';
import { ReactComponent as ArrowLeft } from '../../src/img/BackButton.svg';
import { AdSize } from './AdSize';
import { ScreenSize } from './ScreenSize';
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDeviceSize } from '../hooks/useWindowSize';
import { getScreenSizesWithData, getScreenSizeHasAdSize, getFirstAvailableSize, getSizeToChoose } from '../utils/dataTransformations';
import { SCREEN_SIZES } from '../constants';

const BrandBody = ({ brandData }) => {
  const state = useAppState();
  const navigate = useNavigate();
  const deviceSize = useDeviceSize();
  const locale = state.locale.toLowerCase();
  const linkLocale = locale === 'en' ? '' : locale;
  const [currentSize, setCurrentSize] = useState();
  const [selected, updateSelected] = useState(state.activeOption === 'Video' ? 'Desktop' : 'Mobile');

  // Memoized data transformations
  const screenSizesWithData = useMemo(() => 
    getScreenSizesWithData(state.config, SCREEN_SIZES), 
    [state.config]
  );

  const availableSizes = useMemo(() => 
    state.config?.filter((item) => item.name === selected) || [], 
    [state.config, selected]
  );

  const screenSizeHasAdSize = useMemo(() => 
    getScreenSizeHasAdSize(availableSizes), 
    [availableSizes]
  );

  const firstAvailableSize = useMemo(() => 
    getFirstAvailableSize(brandData, state.adSizes, screenSizeHasAdSize), 
    [brandData, state.adSizes, screenSizeHasAdSize]
  );

  const sizeToChoose = useMemo(() => 
    getSizeToChoose(brandData, firstAvailableSize, screenSizeHasAdSize), 
    [brandData, firstAvailableSize, screenSizeHasAdSize]
  );

  // Automatically select the first available size when screen size changes
  useEffect(() => {
    if (sizeToChoose) {
      setCurrentSize(sizeToChoose);
    }
  }, [selected, sizeToChoose]);

  const goBack = () => {
    navigate(-1);
  }; 

  const expandedView = state.expanded && deviceSize === 'Desktop';
  const expandScreenSize = 'Desktop'; // Always use Desktop for expanded view to show expand/minimize button
  return (
    <div className="boundingBox">
      <div className={`brandBody container ${expandedView ? 'expanded' : null}`}>
        <div className="fullSizeDevice">
          {expandedView && (
            <Device className="marginContainerDevice" selected={expandScreenSize} adSize={currentSize} brandData={brandData} />
          )}
        </div>
        <div className="deviceBackHolder">
          <div className="backButton">
            <span onClick={goBack} className="backbuttonholder">
              <ArrowLeft />
              <span className="goBackCopy">
                <GetCopy copy="back" />
              </span>
            </span>
          </div>

          {!expandedView && (
            <Device className="marginContainerDevice" selected={selected} adSize={currentSize} brandData={brandData} />
          )}
        </div>

        <div className="brandCopy">
          <div className="flex-me">
            <div className="description">
              <div className="holder mb-3">
                <h1>{brandData.Brand}</h1>
                <p>{brandData[`${locale.toUpperCase()} Description`]}</p>
                {!brandData['16:9'] && (
                  <a
                    className="learnMoreAbout"
                    href={`/digital-ad-formats/#${brandData['Format'].toLowerCase().replace(/ /g, '-')}`}
                  >
                    <GetCopy copy="learn more" />
                    &nbsp;{brandData['Format']}
                    &nbsp;
                    <ArrowRight />
                  </a>
                )}
                {!!brandData['16:9'] && (
                  <a href={`/digital-ad-formats/#Video`} className="learnMoreAbout">
                    <GetCopy copy="learn more" />
                    &nbsp;{brandData['Format']}
                    &nbsp;
                    <ArrowRight />
                  </a>
                )}
              </div>
            </div>
            <div className="settings">
              <div className="holder  mb-3">
                <div>
                  <ScreenSize
                    brandData={brandData}
                    ScreenSizes={SCREEN_SIZES}
                    selected={selected}
                    updateSelected={updateSelected}
                    screenSizesWithData={screenSizesWithData}
                  />
                </div>
                <div>
                  <AdSize
                    brandData={brandData}
                    currentSize={currentSize}
                    setCurrentSize={setCurrentSize}
                    selected={selected}
                    screenSizeHasAdSize={screenSizeHasAdSize}
                  />
                </div>
                {brandData['Success Story Path'] && (
                  <div>
                    <GetCopy copy="read the full success story" />
                    <a href={window.location.origin + linkLocale + brandData['Success Story Path']}>
                      &nbsp;
                      <GetCopy copy="here" />
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandBody;
